import { useQuery } from '@tanstack/react-query';
import { endpoints } from 'api/endpoints';
import { isExistingSiteEntryMode } from 'config/entry-mode';
import { useMemo } from 'react';
import { useAppStore } from 'stores/appStore';
import { GetRecommendedProductsHandlerResponse, RecommendedProductsSource } from 'types/recommended-products';
import { SelectableProduct } from 'types/selectable-product';
import { getStaticFallbackProducts, getStaticProductsForCategory } from 'utils/product-catalog';

const RECOMMENDED_PRODUCTS_LIMIT = 8;

type RecommendedCatalog = {
  products: SelectableProduct[];
  source: RecommendedProductsSource;
  searchExpression?: string;
};

function selectRecommendedCatalog(response: GetRecommendedProductsHandlerResponse): RecommendedCatalog {
  if (response.products.length > 0) {
    return {
      products: response.products,
      source: response.source,
      searchExpression: response.searchExpression,
    };
  }

  return {
    products: getStaticFallbackProducts(RECOMMENDED_PRODUCTS_LIMIT),
    source: 'static',
  };
}

export function useProductCatalog() {
  const entryMode = useAppStore((state) => state.entryMode);
  const selectedCategory = useAppStore((state) => state.selectedCategory);
  const metaSiteId = useAppStore((state) => state.metaSiteId);

  const isExistingSiteMode = isExistingSiteEntryMode(entryMode);

  const recommendedQuery = useQuery({
    queryKey: ['recommended-products', metaSiteId],
    queryFn: () => endpoints.getRecommendedProducts({ limit: RECOMMENDED_PRODUCTS_LIMIT }),
    enabled: isExistingSiteMode && Boolean(metaSiteId),
    staleTime: Infinity,
    select: selectRecommendedCatalog,
  });

  const { data, isPending, isSuccess, isError } = recommendedQuery;

  const products = useMemo((): SelectableProduct[] => {
    if (!isExistingSiteMode) {
      return getStaticProductsForCategory(selectedCategory);
    }

    if (data) {
      return data.products;
    }

    if (isError) {
      return getStaticFallbackProducts(RECOMMENDED_PRODUCTS_LIMIT);
    }

    return [];
  }, [isExistingSiteMode, selectedCategory, data, isError]);

  const isLoading = isExistingSiteMode && isPending;
  const isReady = !isExistingSiteMode || isSuccess || isError;

  const productsSource: RecommendedProductsSource | undefined = isExistingSiteMode
    ? data?.source ?? (isError ? 'static' : undefined)
    : undefined;

  const searchExpression = isExistingSiteMode ? data?.searchExpression : undefined;

  return {
    products,
    isLoading,
    isReady,
    productsSource,
    searchExpression,
  };
}
